/**
 * Утилита для работы с аудиофайлами:
 * - получение длительности из метаданных
 * - расчёт времени обработки (ETA)
 * - форматирование длительности и размера
 */

/**
 * Получить длительность аудиофайла из метаданных браузера.
 * Использует Audio API, поддерживает основные форматы (MP3, WAV, OGG, M4A).
 *
 * @param {File} file - объект File из input[type="file"]
 * @returns {Promise<number|null>} длительность в секундах или null если формат не поддерживается
 * @throws {Error} если произошла ошибка чтения файла
 */
export async function getAudioDuration(file) {
  return new Promise((resolve) => {
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);

    // Таймаут 3 секунды на случай, если браузер долго загружает метаданные
    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, 3000);

    const cleanup = () => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(objectUrl);
      audio.src = '';
      audio.onloadedmetadata = null;
      audio.onerror = null;
    };

    audio.onloadedmetadata = () => {
      const duration = audio.duration;
      cleanup();
      // Проверяем, что duration — валидное число и не Infinity
      resolve(isFinite(duration) ? duration : null);
    };

    audio.onerror = () => {
      cleanup();
      resolve(null);
    };

    audio.src = objectUrl;
    // Начинаем загрузку метаданных (не весь файл)
    audio.load();
  });
}

/**
 * Расчитать приблизительное время обработки файла.
 * RTF (Real Time Factor) без диаризации: 0.022
 * RTF с диаризацией: 0.08
 * Минимальное время: 1 минута.
 *
 * @param {number} durationSec - длительность аудио в секундах
 * @param {boolean} [withDiarization=false] - использует ли диаризацию
 * @returns {number} ETA в минутах
 */
export function estimateProcessingTime(durationSec, withDiarization = false) {
  if (typeof durationSec !== 'number' || durationSec < 0) {
    return 1;
  }

  const rtf = withDiarization ? 0.096 : 0.0264;
  const processingSeconds = durationSec * rtf;
  const processingMinutes = processingSeconds / 60;

  // Минимум 1 минута
  return Math.max(1, Math.ceil(processingMinutes));
}

/**
 * Форматировать длительность в читаемый вид.
 * Примеры: "1ч 2м", "3м 24с", "45с"
 *
 * @param {number} seconds - длительность в секундах
 * @returns {string|null} отформатированная строка или null для невалидного ввода
 */
export function formatDuration(seconds) {
  if (typeof seconds !== 'number' || seconds < 0 || !isFinite(seconds)) {
    return null;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}ч`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}м`);
  }

  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs}с`);
  }

  return parts.join(' ');
}

/**
 * Форматировать размер файла в читаемый вид.
 * Примеры: "47 МБ", "1.2 ГБ", "512 КБ"
 *
 * Единицы русские: «184.0 MB» посреди русского интерфейса — латиница там,
 * где её быть не должно.
 *
 * @param {number} bytes - размер в байтах
 * @returns {string|null} отформатированная строка или null для невалидного ввода
 */
export function formatFileSize(bytes) {
  if (typeof bytes !== 'number' || bytes < 0 || !isFinite(bytes)) {
    return null;
  }

  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  // Для байтов и килобайтов показываем без десятичных
  if (unitIndex <= 1) {
    return `${Math.round(size)} ${units[unitIndex]}`;
  }

  // Для больших размеров — одна цифра после запятой, но не «184.0»: дробная
  // часть нужна там, где она что-то говорит.
  const rounded = Math.round(size * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} ${units[unitIndex]}`;
}
